/*
  NUMU Media 2026: larger design preview on collection / search grids.

  Desktop (real hover + fine pointer): hovering a card image shows a larger
  preview floating next to the card. Related products on the product page
  (data-mode="gallery") show the design in the main product image instead,
  like APO's Papirkvalitet hover. It is position:fixed and pointer-events:none,
  so the grid never moves and the product link keeps working.

  Touch / keyboard: the small magnifier button on the card image opens the
  preview in a lightweight native <dialog>. Tapping anywhere else on the card
  opens the product as normal.

  Event delegation on document, so it keeps working after the filters re-render
  the product grid. No dependencies.
*/
(function () {
  'use strict';
  if (window.__numuCardPreview) return;
  window.__numuCardPreview = true;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  var HOVER_DELAY = 180;
  var PANEL = 560; // px, max width of the hover preview
  var panel, panelImg, timer, currentCard;

  function dataFrom(card) {
    return card && card.querySelector('[data-numu-preview]');
  }

  /* ---------- Desktop hover panel ---------- */
  function ensurePanel() {
    if (panel) return;
    panel = document.createElement('div');
    panel.className = 'numu-hover-preview';
    panel.setAttribute('aria-hidden', 'true');
    panelImg = document.createElement('img');
    panelImg.alt = '';
    panelImg.decoding = 'async';
    panel.appendChild(panelImg);
    document.body.appendChild(panel);
  }

  function place(card, ratio) {
    var r = card.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    var vh = window.innerHeight;
    var gap = 16;
    var w = Math.min(PANEL, Math.max(r.width * 1.7, 360));
    var h = w / (ratio || 1);
    if (h > vh - 2 * gap) { h = vh - 2 * gap; w = h * (ratio || 1); }
    var left = r.right + gap;
    if (left + w > vw - gap) left = r.left - gap - w;          // not enough room right: go left
    if (left < gap) return false;                               // no room either side: skip
    var top = r.top + r.height / 2 - h / 2;
    top = Math.max(gap, Math.min(top, vh - h - gap));
    panel.style.width = Math.round(w) + 'px';
    panel.style.height = Math.round(h) + 'px';
    panel.style.left = Math.round(left) + 'px';
    panel.style.top = Math.round(top) + 'px';
    return true;
  }

  function show(card) {
    var d = dataFrom(card);
    if (!d) return;
    ensurePanel();
    var ratio = parseFloat(d.getAttribute('data-ratio')) || 1;
    if (!place(card, ratio)) return;
    panelImg.sizes = PANEL + 'px';
    panelImg.srcset = d.getAttribute('data-srcset') || '';
    panelImg.src = d.getAttribute('data-src');
    panel.classList.add('is-visible');
  }

  /* ---------- Product page: Papirkvalitet-style preview in the main gallery ----------
     Same behaviour as APO's "replace on hover" on Papirkvalitet: while hovering a
     related product image, the big product image shows that design. Restored on leave.
     If the main image is not in view, the floating panel is used instead. */
  var swappedImg = null;

  function galleryImage() {
    var imgs = document.querySelectorAll(
      '.product__media-wrapper .product__media-item.is-active .product__media img, .product__media-wrapper .product__media-list .product__media img'
    );
    for (var i = 0; i < imgs.length; i++) {
      var r = imgs[i].getBoundingClientRect();
      if (r.width > 120 && r.height > 120) return imgs[i];
    }
    return null;
  }

  function mostlyInView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight;
    var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    return visible > r.height * 0.5;
  }

  function restoreGallery() {
    if (!swappedImg) return;
    var o = swappedImg.__numuOrig;
    if (o) {
      if (o.srcset) swappedImg.srcset = o.srcset; else swappedImg.removeAttribute('srcset');
      swappedImg.src = o.src;
    }
    swappedImg.classList.remove('numu-gallery-swapped');
    swappedImg = null;
  }

  function showInGallery(card) {
    var d = dataFrom(card);
    var img = galleryImage();
    if (!d || !img || !mostlyInView(img)) return false;
    if (!img.__numuOrig) img.__numuOrig = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
    img.srcset = d.getAttribute('data-srcset') || '';
    img.src = d.getAttribute('data-src');
    img.classList.add('numu-gallery-swapped');
    swappedImg = img;
    return true;
  }

  function hide() {
    clearTimeout(timer);
    currentCard = null;
    if (panel) panel.classList.remove('is-visible');
    restoreGallery();
  }

  document.addEventListener('mouseover', function (e) {
    if (!canHover.matches) return;
    var card = e.target.closest && e.target.closest('.card__inner');
    if (!card || !dataFrom(card)) { if (currentCard) hide(); return; }
    if (card === currentCard) return;
    hide();
    currentCard = card;
    timer = setTimeout(function () {
      if (currentCard !== card) return;
      var d = dataFrom(card);
      if (d && d.getAttribute('data-mode') === 'gallery' && showInGallery(card)) return;
      show(card);
    }, HOVER_DELAY);
  });
  document.addEventListener('mouseleave', hide);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
  window.addEventListener('scroll', function () { if (panel && panel.classList.contains('is-visible')) hide(); }, { passive: true });

  /* ---------- Touch / keyboard dialog ---------- */
  var dialog, dImg, dTitle, dLink;
  function ensureDialog() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'numu-preview-dialog';
    dialog.innerHTML =
      '<div class="numu-preview-dialog__inner">' +
      '<button type="button" class="numu-preview-dialog__close" aria-label="Luk">' +
      '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg></button>' +
      '<img class="numu-preview-dialog__img" alt="" decoding="async">' +
      '<div class="numu-preview-dialog__footer"><p class="numu-preview-dialog__title"></p>' +
      '<a class="numu-preview-dialog__link" href="#">Se produktet</a></div></div>';
    document.body.appendChild(dialog);
    dImg = dialog.querySelector('img');
    dTitle = dialog.querySelector('.numu-preview-dialog__title');
    dLink = dialog.querySelector('.numu-preview-dialog__link');
    dialog.querySelector('.numu-preview-dialog__close').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); }); // click on backdrop
    dialog.addEventListener('close', function () { document.documentElement.classList.remove('numu-preview-open'); });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-numu-preview]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    hide();
    ensureDialog();
    dImg.sizes = 'min(92vw, 720px)';
    dImg.srcset = btn.getAttribute('data-srcset') || '';
    dImg.src = btn.getAttribute('data-src');
    dImg.alt = btn.getAttribute('data-alt') || '';
    dTitle.textContent = btn.getAttribute('data-title') || '';
    dLink.href = btn.getAttribute('data-url') || '#';
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      document.documentElement.classList.add('numu-preview-open');
    } else {
      window.location.href = dLink.href; // very old browsers: just open the product
    }
  }, true);
})();
